import type { AttachmentFit, TowerKit } from "../kit/types";
import type { FitSpec } from "../kit/bigTypes";
import { ROOFED, SLENDER } from "./towerGroups";

/**
 * Gilded (free at tower Lv 60): royal gold (contract tools/art/skins/README.md; big kit, §11).
 *
 * The look: honey marble veined in gold, lacquered walnut galleries, polished gold fittings and crimson cloth. The stock
 * roof is hidden and a ROYAL CROWN takes its place — an ermine roll, a jewelled gold circlet with fleur points, a crimson
 * velvet cap under four gold arches, an orb and cross on top — raised on gold columns where the roof storey holds the
 * bow, the brew or the beacon's orb; the fire tower keeps its chimney, gilded, and wears a small crown on it (the chimney
 * stands in front of the brazier). Crimson festoons hang round the cannon, frost and fire drums (2026-10-22). Gold bells hang from the drum's cornice and swing (Motion.flap about their loops: the
 * skin's own motion), royal banners wave on gold poles at the front plinth corners, a gold cornice with crimson panels
 * and gold pilasters clads the drum, and coin glints twinkle. Frost and lightning (no roof the kit may hide) wear the
 * crown's jewelled circlet round the crystal / the spire's foot. In a level: coin glints wink round the crown, and the
 * shots land in a golden radiant burst, then a glint, in turn. Props: art/hq/skins/gilded/kit.glb (gilded.py).
 */
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the crown stands on columns there. */
const PAVILIONS = ["archer", "poison", "beacon"];
/**
 * The fire tower's roof is its tall chimney, on the camera's side of the brazier (README §2): a crown in its place hid
 * the flame, so the fire tower keeps its chimney (gilded by the roof slot) and wears a small crown on its top.
 */
const FIRE_CROWN = { scale: [0.36, 0.4, 0.44], lift: -0.02 } as const;
const CROWNED = [...ROOFED, "arcane"].filter((t) => !PAVILIONS.includes(t) && t !== "fire");
const CROWN_MARGIN = 0.04;
/** The barracks' roof envelope is its hall plus a turret pole: the crown is fitted to the hall's box (see verdigris.ts). */
const BARRACKS_CROWN: AttachmentFit = { fitTo: { to: "roof", shape: "box", margin: 0.5 }, scale: 0.65 };
const BEACON_RISE = 0.27;
/** Frost's circlet rings the battlement's crenels; lightning's sits on the deck under its roof. */
const CIRCLET: FitSpec = { to: "gallery", height: "keep", margin: 0.06 };
/** The bells swing out and back about their loops (deg, Hz); every bell has its own phase. */
const BELL = { flap: { deg: 20, axis: "right", hz: 0.6 } } as const;
/** The banners' cloth: a travelling wave and a slow sway. */
const BANNER = { wave: 0.03, waveK: 22, hz: 0.6 } as const;
/** Banners on the two plinth corners nearest the camera at the default turn (one of them at the other 45° turn). */
const BANNER_CORNERS = ["corner2", "corner3"] as const;
/**
 * The cannon, frost and fire tops are the barrel, the crystal and the brazier (the crown sits on a small back roof, or
 * nowhere), so their drums carry the royal look: crimson festoons hung under the frieze's cornice between ruby rosettes,
 * their gold hems billowing (the drape).
 */
const DRAPED = ["cannon", "frost", "fire"];
const DRAPE_FIT: FitSpec = { to: "drum", taper: true, margin: 0.02 };
const DRAPE_WAVE = { wave: 0.014, waveK: 9, hz: 0.45 } as const;

export const GILDED: TowerKit = {
  id: "tower.gilded",
  asset: "skins/gilded/kit",
  hide: ["roof"],
  hideFit: { hex: [], fire: [] },
  tier4: true,
  slots: {
    stone: { tint: 0xeedfbd, mix: 0.6, gain: 1.1, roughness: 0.45, trim: { colour: 0xffd27a, strength: 0.6 }, pattern: { kind: "veins", colour: 0xe0a93a, scale: 4.5, strength: 0.9, glow: 0.3 } },
    roof: { tint: 0xf2c24e, mix: 0.92, gain: 1.15, metalness: 0.95, roughness: 0.28, keepAccents: false, trim: { colour: 0xffd98a, strength: 0.7 }, pattern: { kind: "hammered", colour: 0xffeab0, scale: 24, strength: 0.4 } },
    wood: { tint: 0x6e3b1d, mix: 0.6, gain: 0.8, roughness: 0.34, trim: { colour: 0xffcf6a, strength: 0.8 }, pattern: { kind: "grain", colour: 0x3b1c0b, scale: 9, strength: 0.45 } },
    metal: { tint: 0xffd060, mix: 0.92, gain: 1.15, metalness: 1, roughness: 0.24, keepAccents: false, trim: { colour: 0xffe08a, strength: 0.5 } },
    cloth: { tint: 0xa3121f, mix: 0.8, gain: 1.05, roughness: 0.7, keepAccents: false, trim: { colour: 0xffd27a, strength: 0.5 } },
  },
  attachments: [
    { piece: "crown", at: [], towers: CROWNED, ifHidden: "roof", fitTo: { to: "roof", margin: CROWN_MARGIN }, fit: { barracks: BARRACKS_CROWN } },
    { piece: "crownpav", at: [], towers: PAVILIONS, ifHidden: "roof", fitTo: { to: "roof", margin: CROWN_MARGIN }, fit: { beacon: { offset: [0, BEACON_RISE, 0] } } },
    { piece: "crown", at: ["apex"], towers: ["fire"], scale: FIRE_CROWN.scale, offset: [0, FIRE_CROWN.lift, 0] },
    { piece: "circlet", at: [], towers: ["frost", "lightning"], fitTo: CIRCLET, fit: { frost: { offset: [0, 0.24, 0] } } },
    // the hex totem keeps its feather crown: the circlet rings its pole's top under the mask
    { piece: "circlet", at: ["top"], towers: ["hex"], offset: [0, -0.1, 0], scale: [0.36, 0.38, 0.4] },
    { piece: "frieze", at: [], fitTo: { to: "drum", taper: true, margin: 0.02 }, fit: { barracks: { fitTo: { to: "drum", shape: "box", margin: 0.05 } } } },
    { piece: "drape", at: [], towers: DRAPED, fitTo: DRAPE_FIT, motion: DRAPE_WAVE },
    // gold bells hung from the drum's gold cornice on all four diagonals, swinging on their loops
    { piece: "bell", at: ["band"], offset: [0, 0.06, 0.06], scale: [2.1, 2.3, 2.5], motion: BELL, fit: {
      arcane: { offset: [0, 0.02, 0.04], scale: [1.5, 1.6, 1.7] }, hex: { offset: [0, 0.02, 0.04], scale: [1.5, 1.6, 1.7] },
    } },
    { piece: "banner", at: BANNER_CORNERS, scale: [1.45, 1.6, 1.75], yaw: -30, motion: BANNER, fit: { arcane: { scale: [1.3, 1.4, 1.5] }, hex: { scale: [1.3, 1.4, 1.5] } } },
    { piece: "glint", at: ["apex"], offset: [0.14, 0.2, 0.06], scale: 1.4, detail: true, motion: { twinkle: 0.32 }, fit: { arcane: { offset: [0.2, 0.5, 0.06] }, hex: { offset: [0.16, 0.42, 0.06] }, beacon: { offset: [0.14, 0.2 + BEACON_RISE, 0.06] } } },
    { piece: "glint", at: ["wall1", "wall3"], offset: [0.06, 0.07, 0.04], scale: 1.2, detail: true, motion: { twinkle: 0.21 } },
    { piece: "glint", at: ["corner0", "corner3"], offset: [0.0, 0.5, 0.04], scale: 1.0, detail: true, motion: { twinkle: 0.27 }, towers: [...ROOFED, "frost", ...SLENDER] },
  ],
  idle: { kind: "glints", colour: 0xffd77a, every: [1.4, 2.4], at: "apex", count: 5, radius: 0.3 },
  // a golden radiant burst, then a glint, in turn; a gold ring at the muzzle
  flourish: {
    impacts: [{ kind: "radiant", colour: 0xffd27a, count: 8 }, { kind: "glint", colour: 0xffe7a0, count: 6 }],
    muzzle: { kind: "ring", colour: 0xffe7a0, count: 3 },
  },
  preview: { tower: "archer", tier: 3 },
};
